"use client";

import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TrustedByPetParents() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const featuredTestimonial = {
    quote: "Fit Pet Guide completely changed how I manage my dog's weight. The practical tips and the weight tracker made the process so much easier and less stressful for both of us.",
    author: "Sarah M.",
    role: "DOG OWNER",
    note: "Successfully helped her Golden Retriever reach a healthy weight in 3 months",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704107/img/happy_dog_owner.webp",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704107/img/happy_dog_owner.webp-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704107/img/happy_dog_owner.webp-wvc-sizes"
    }
  };
  const carouselTestimonials = [{
    quote: "The cat weight loss guide is a game changer. My vet was impressed with the progress we made using the simple feeding tips provided here.",
    author: "David K.",
    role: "CAT PARENT"
  }, {
    quote: "I love the community and the non-judgmental approach. It feels like having a supportive partner in my pet's wellness journey.",
    author: "Elena R.",
    role: "PET WELLNESS ADVOCATE"
  }, {
    quote: "The printable trackers are so helpful. They keep me organized and motivated to stay consistent with my dog's exercise routine.",
    author: "Marcus T.",
    role: "DOG OWNER"
  }, {
    quote: "Fit Pet Guide doesn't just give advice; it teaches you how to understand your pet's needs. It's truly a holistic approach to pet health.",
    author: "Jessica L.",
    role: "CAT PARENT"
  }];
  const [activeIndex, setActiveIndex] = useState(0);
  const handlePrev = () => {
    setActiveIndex(prev => prev === 0 ? carouselTestimonials.length - 1 : prev - 1);
  };
  const handleNext = () => {
    setActiveIndex(prev => prev === carouselTestimonials.length - 1 ? 0 : prev + 1);
  };
  return <section data-section-id="3529" id="trusted-by-pet-parents" className="bg-background text-foreground py-24 md:py-36 lg:py-44 px-6 md:px-12 lg:px-20">
      <div ref={sectionRef} className={`max-w-7xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {}
        <div className="text-center mb-20 md:mb-28">
          <p className="font-default text-xs md:text-sm tracking-[0.3em] text-muted-foreground uppercase">
            06 <span className="mx-3">—</span> Trusted by Pet Parents
          </p>
        </div>

        {}
        <div className="relative max-w-[880px] mx-auto mb-24 md:mb-32 lg:mb-40">
          {}
          <div className="absolute -top-8 md:-top-12 -left-2 md:-left-8 pointer-events-none select-none" aria-hidden="true">
            <span className="font-default leading-none" style={{
            fontSize: "6rem",
            color: "var(--primary)",
            lineHeight: 1
          }}>
              &ldquo;
            </span>
          </div>

          {}
          <blockquote className="relative px-4 md:px-12">
            <p className="font-default italic text-center text-foreground leading-[1.4] md:leading-[1.45]" style={{
            fontSize: "clamp(1.5rem, 3vw, 2.5rem)"
          }}>
              {featuredTestimonial.quote}
            </p>
          </blockquote>

          {}
          <div className="absolute -bottom-16 md:-bottom-20 -right-2 md:-right-8 pointer-events-none select-none" aria-hidden="true">
            <span className="font-default leading-none" style={{
            fontSize: "6rem",
            color: "var(--primary)",
            lineHeight: 1
          }}>
              &rdquo;
            </span>
          </div>

          {}
          <div className="flex flex-col items-center mt-16 md:mt-20">
            <div className="w-20 h-20 overflow-hidden mb-6 border border-border">
              <img src={featuredTestimonial.image.src} data-wvc-srcset={featuredTestimonial.image.srcset} data-wvc-sizes={featuredTestimonial.image.sizes} alt={featuredTestimonial.author} loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>
            <p className="font-default text-xl md:text-2xl text-foreground mb-2">
              {featuredTestimonial.author}
            </p>
            <p className="font-default text-xs tracking-[0.25em] text-muted-foreground uppercase mb-4">
              {featuredTestimonial.role}
            </p>
            <p className="font-default italic text-sm text-muted-foreground text-center max-w-md">
              {featuredTestimonial.note}
            </p>
          </div>
        </div>

        {}
        <div className="max-w-xs mx-auto mb-20 md:mb-24">
          <div className="h-px bg-border" />
        </div>

        {}
        <div className="relative max-w-6xl mx-auto">
          {}
          <Button variant="ghost" size="icon" onClick={handlePrev} aria-label="Previous testimonial" className="absolute left-0 md:-left-4 lg:-left-8 top-1/2 -translate-y-1/2 z-10 text-foreground hover:bg-transparent hover:text-primary h-12 w-12">
            <ChevronLeft className="h-6 w-6" strokeWidth={1} />
          </Button>

          <Button variant="ghost" size="icon" onClick={handleNext} aria-label="Next testimonial" className="absolute right-0 md:-right-4 lg:-right-8 top-1/2 -translate-y-1/2 z-10 text-foreground hover:bg-transparent hover:text-primary h-12 w-12">
            <ChevronRight className="h-6 w-6" strokeWidth={1} />
          </Button>

          {}
          <div className="overflow-hidden px-8 md:px-16">
            <div className="flex transition-transform duration-700 ease-in-out" style={{
            transform: `translateX(-${activeIndex * 100}%)`
          }}>
              {carouselTestimonials.map((testimonial, i) => <div key={i} data-index={i} className="min-w-full md:min-w-[50%] lg:min-w-[33.333%] px-3 md:px-4">
                  <div className="border border-border p-8 md:p-10 h-full flex flex-col">
                    <Quote className="h-5 w-5 text-primary mb-6" strokeWidth={1.5} aria-hidden="true" />
                    <p className="font-default italic text-base md:text-lg text-foreground leading-relaxed mb-8 flex-grow">
                      {testimonial.quote}
                    </p>
                    <div className="pt-6 border-t border-border">
                      <p className="font-default text-lg text-foreground mb-1">
                        {testimonial.author}
                      </p>
                      <p className="font-default text-[10px] tracking-[0.25em] text-muted-foreground uppercase">
                        {testimonial.role}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>

          {}
          <div className="flex justify-center items-center gap-3 mt-12 md:mt-16">
            {carouselTestimonials.map((_, i) => <div key={i} data-index={i}>
                <Button variant="ghost" size="sm" onClick={() => setActiveIndex(i)} aria-label={`Go to testimonial ${i + 1}`} className={`transition-all duration-300 p-0 h-auto ${activeIndex === i ? "w-8 h-[2px] bg-primary hover:bg-primary" : "w-2 h-2 bg-muted-foreground/40 hover:bg-muted-foreground rounded-lg"}`} />
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}